import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Sparkles, LayoutDashboard, Settings, LogOut, Menu, PenTool, Zap, FileText, Target, TrendingUp, Shield, Star } from 'lucide-react'
import StatsOverview from '../components/dashboard/StatsOverview'
import UsageChart from '../components/dashboard/UsageChart'
import ClicheBreakdown from '../components/dashboard/ClicheBreakdown'
import ImprovementScore from '../components/dashboard/ImprovementScore'
import ActivityCalendar from '../components/dashboard/ActivityCalendar'
import WeeklyReport from '../components/dashboard/WeeklyReport'
import ExtensionBanner from '../components/dashboard/ExtensionBanner'
import SubscriptionBanner from '../components/SubscriptionBanner'
import NotificationCenter from '../components/NotificationCenter'
import { NotificationProvider } from '../contexts/NotificationContext'
import UpgradeModal from '../components/UpgradeModal'
import LifetimeCheckoutModal from '../components/LifetimeCheckoutModal'
import FeedbackWidget from '../components/FeedbackWidget'
import VoiceGuide from '../components/VoiceGuide'
import { signOut, getUser, isProUser, getSubscriptionDetails, getSubscriptionStatus } from '../utils/auth'
import { supabase, isSupabaseConfigured } from '../utils/supabase'
import { isAdminUser } from '../utils/admin'
import { trackDashboardVisit } from '../utils/visitTracker'

export default function DashboardPage() {
    const navigate = useNavigate()
    const [user, setUser] = useState(null)
    const [isPro, setIsPro] = useState(false)
    const [isAdmin, setIsAdmin] = useState(false)
    const [subscriptionStatus, setSubscriptionStatus] = useState({ status: 'free', daysRemaining: null })
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [showUpgradeModal, setShowUpgradeModal] = useState(false)
    const [showLifetimeModal, setShowLifetimeModal] = useState(false)
    const [dashboardData, setDashboardData] = useState({
        stats: null,
        usageData: null,
        clicheData: null,
        score: 0,
        previousScore: 0,
    })

    useEffect(() => {
        const fetchUser = async () => {
            const currentUser = await getUser()
            setUser(currentUser)
            if (currentUser) {
                const proStatus = await isProUser(currentUser.id)
                setIsPro(proStatus)
                setIsAdmin(isAdminUser(currentUser.email))
                await fetchDashboardData(currentUser.id)

                // Fetch subscription status for expiration warnings
                const subDetails = await getSubscriptionDetails(currentUser.id, currentUser.email)
                if (subDetails) {
                    setSubscriptionStatus(getSubscriptionStatus(subDetails))
                }

                // Track dashboard visit (silent, server-side geolocation)
                trackDashboardVisit(currentUser.id)
            }
        }
        fetchUser()
    }, [])

    const fetchDashboardData = async (userId) => {
        if (!isSupabaseConfigured()) {
            // Set empty data if Supabase not configured
            setDashboardData({
                stats: [
                    { label: 'Words Analyzed', value: '0', change: '-', changeType: 'neutral', icon: FileText, color: 'primary' },
                    { label: 'Clichés Detected', value: '0', change: '-', changeType: 'neutral', icon: Target, color: 'accent' },
                    { label: 'Writing Score', value: '0', change: '-', changeType: 'neutral', icon: TrendingUp, color: 'green' },
                    { label: 'AI Requests', value: '0', change: '-', changeType: 'neutral', icon: Zap, color: 'yellow' },
                ],
                usageData: [],
                clicheData: [],
                score: 0,
                previousScore: 0,
            })
            return
        }

        try {
            // Fetch user's analytics data
            const [analyticsResult, dailyStatsResult] = await Promise.all([
                supabase.from('user_analytics').select('*').eq('user_id', userId),
                supabase.from('daily_stats').select('*').eq('user_id', userId).order('date', { ascending: false }).limit(7),
            ])

            const userAnalytics = analyticsResult.data || []
            const dailyStats = dailyStatsResult.data || []

            // Calculate totals from analytics
            const totalWords = userAnalytics.reduce((sum, u) => sum + (u.words_analyzed || 0), 0)
            const totalCliches = userAnalytics.reduce((sum, u) => sum + (u.cliches_detected || 0), 0)
            const totalSuggestions = userAnalytics.reduce((sum, u) => sum + (u.suggestions_used || 0), 0)
            const latestScore = userAnalytics[0]?.quality_score || 0

            // Calculate AI requests from daily usage (from localStorage or database)
            const todayKey = new Date().toISOString().split('T')[0]
            const localUsage = localStorage.getItem(`inkspot_usage_${todayKey}`)
            const aiRequests = localUsage ? JSON.parse(localUsage).aiRequests || 0 : 0

            // Format stats for StatsOverview component
            const stats = [
                {
                    label: 'Words Analyzed',
                    value: totalWords.toLocaleString(),
                    change: totalWords > 0 ? '+' + Math.round(totalWords * 0.1) : '-',
                    changeType: totalWords > 0 ? 'positive' : 'neutral',
                    icon: FileText,
                    color: 'primary',
                },
                {
                    label: 'Clichés Detected',
                    value: totalCliches.toString(),
                    change: totalCliches > 0 ? '-' + Math.round(totalCliches * 0.05) : '-',
                    changeType: totalCliches > 0 ? 'positive' : 'neutral',
                    icon: Target,
                    color: 'accent',
                },
                {
                    label: 'Writing Score',
                    value: Math.round(latestScore).toString() || '0',
                    change: latestScore > 0 ? '+' + Math.round(latestScore * 0.05) : '-',
                    changeType: latestScore > 0 ? 'positive' : 'neutral',
                    icon: TrendingUp,
                    color: 'green',
                },
                {
                    label: 'AI Requests',
                    value: aiRequests.toString(),
                    change: isPro ? 'Unlimited' : '10/day',
                    changeType: 'neutral',
                    icon: Zap,
                    color: 'yellow',
                },
            ]

            // Format usage data for chart
            const usageData = dailyStats.reverse().map(d => ({
                date: new Date(d.date).toLocaleDateString('en-US', { weekday: 'short' }),
                words: d.words_analyzed || 0,
                cliches: d.cliches_found || 0,
            }))

            setDashboardData({
                stats,
                usageData: usageData.length > 0 ? usageData : null,
                clicheData: null, // Would need cliche_stats table
                score: Math.round(latestScore) || 0,
                previousScore: Math.round((latestScore || 0) * 0.95),
            })

        } catch (error) {
            console.error('Error fetching dashboard data:', error)
            // Set empty data on error
            setDashboardData({
                stats: [
                    { label: 'Words Analyzed', value: '0', change: '-', changeType: 'neutral', icon: FileText, color: 'primary' },
                    { label: 'Clichés Detected', value: '0', change: '-', changeType: 'neutral', icon: Target, color: 'accent' },
                    { label: 'Writing Score', value: '0', change: '-', changeType: 'neutral', icon: TrendingUp, color: 'green' },
                    { label: 'AI Requests', value: '0', change: '-', changeType: 'neutral', icon: Zap, color: 'yellow' },
                ],
                usageData: [],
                clicheData: [],
                score: 0,
                previousScore: 0,
            })
        }
    }

    const handleSignOut = async () => {
        await signOut()
        navigate('/')
    }

    return (
        <NotificationProvider user={user} subscriptionStatus={subscriptionStatus}>
            <div className="min-h-screen bg-slate-950 flex">
                {/* Sidebar */}
                <aside
                    className={`fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 border-r border-white/10 transform transition-transform duration-300 lg:translate-x-0 lg:static
                    ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
                >
                    <div className="flex flex-col h-full">
                        {/* Logo */}
                        <div className="flex items-center gap-2 px-6 py-5 border-b border-white/10">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-xl font-bold text-white">InkSpot</span>
                            {isPro && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-full">
                                    PRO
                                </span>
                            )}
                        </div>

                        {/* Nav Links */}
                        <nav className="flex-grow px-4 py-6">
                            <ul className="space-y-2">
                                <li>
                                    <Link
                                        to="/app"
                                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 hover:text-white transition-colors"
                                    >
                                        <PenTool className="w-5 h-5" />
                                        Editor
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/dashboard"
                                        className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/10 text-white font-medium"
                                    >
                                        <LayoutDashboard className="w-5 h-5" />
                                        Dashboard
                                    </Link>
                                </li>
                                <li>
                                    <Link
                                        to="/settings"
                                        className="flex items-center gap-3 px-4 py-3 rounded-xl text-white/60 hover:bg-white/5 hover:text-white transition-colors"
                                    >
                                        <Settings className="w-5 h-5" />
                                        Settings
                                    </Link>
                                </li>
                                {isAdmin && (
                                    <li>
                                        <Link
                                            to="/admin"
                                            className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-400/70 hover:bg-red-500/10 hover:text-red-400 transition-colors"
                                        >
                                            <Shield className="w-5 h-5" />
                                            Admin Panel
                                        </Link>
                                    </li>
                                )}
                            </ul>
                        </nav>

                        {/* User Section */}
                        <div className="p-4 border-t border-white/10">
                            <div className="flex items-center gap-3 px-2 mb-4">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-accent-500 flex items-center justify-center text-white font-bold">
                                    {user?.email?.charAt(0).toUpperCase() || 'U'}
                                </div>
                                <div className="flex-grow min-w-0">
                                    <p className="text-sm font-medium text-white truncate">
                                        {user?.email || 'User'}
                                    </p>
                                    <p className="text-xs text-white/50">
                                        {isPro ? 'Pro Plan' : 'Free Plan'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleSignOut}
                                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-white/60 hover:bg-white/5 hover:text-white transition-colors"
                            >
                                <LogOut className="w-5 h-5" />
                                Sign out
                            </button>
                        </div>
                    </div>
                </aside>

                {/* Mobile Sidebar Overlay */}
                {sidebarOpen && (
                    <div
                        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                {/* Main Content */}
                <main className="flex-grow min-h-screen overflow-y-auto">
                    {/* Mobile Header */}
                    <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-white/10">
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="p-2 text-white/70 hover:text-white"
                        >
                            <Menu className="w-6 h-6" />
                        </button>
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-primary-400" />
                            <span className="font-bold text-white">InkSpot</span>
                        </div>
                        <NotificationCenter />
                    </header>

                    {/* Dashboard Content */}
                    <div className="p-4 lg:p-8">
                        <div className="max-w-7xl mx-auto">
                            {/* Desktop Header with Notifications */}
                            <div className="hidden lg:flex items-center justify-end mb-4">
                                <NotificationCenter />
                            </div>

                            {/* Page Header */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                                <div>
                                    <h1 className="text-2xl font-bold text-white mb-2">
                                        Analytics Dashboard
                                    </h1>
                                    <p className="text-white/60">
                                        Track your writing improvement and see detailed statistics.
                                    </p>
                                </div>
                                {!isPro && (
                                    <button
                                        onClick={() => setShowUpgradeModal(true)}
                                        className="btn-primary flex items-center gap-2 whitespace-nowrap"
                                    >
                                        <Zap className="w-4 h-4" />
                                        Upgrade to Pro
                                    </button>
                                )}
                            </div>

                            {/* Subscription Expiration Banner */}
                            <SubscriptionBanner 
                                subscriptionStatus={subscriptionStatus} 
                                isPro={isPro} 
                                onRenew={() => setShowUpgradeModal(true)}
                            />

                            {/* Chrome Extension Banner */}
                            <ExtensionBanner isPro={isPro} />

                            {/* Stats Overview */}
                            <div className="mb-8" data-tutorial="dashboard-stats">
                                <StatsOverview data={dashboardData.stats} />
                            </div>

                            {/* Charts Row */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                                <div className="lg:col-span-2" data-tutorial="usage-chart">
                                    <UsageChart data={dashboardData.usageData} />
                                </div>
                                <div data-tutorial="improvement-score">
                                    <ImprovementScore score={dashboardData.score} previousScore={dashboardData.previousScore} />
                                </div>
                            </div>

                            {/* Bottom Row */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                                <ClicheBreakdown data={dashboardData.clicheData} />
                                <WeeklyReport />
                            </div>

                            {/* Activity Calendar */}
                            <div className="mb-8">
                                <ActivityCalendar />
                            </div>

                            {/* Pro Upgrade CTA (for free users) */}
                            {!isPro && (
                                <div className="glass-card p-6 bg-gradient-to-r from-primary-500/10 to-accent-500/10 border-primary-500/30">
                                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                        <div>
                                            <h3 className="text-lg font-semibold text-white mb-1">
                                                Unlock Full Analytics
                                            </h3>
                                            <p className="text-white/60">
                                                Upgrade to Pro for unlimited AI requests, detailed reports, and custom style guides.
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => setShowUpgradeModal(true)}
                                            className="btn-primary whitespace-nowrap flex items-center gap-2"
                                        >
                                            <Zap className="w-4 h-4" />
                                            Upgrade to Pro
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Founding Member Lifetime Deal (for free users) */}
                            {!isPro && (
                                <div className="glass-card p-6 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-yellow-500/30 mt-4">
                                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                                                <Star className="w-6 h-6 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold text-white mb-1">
                                                    🔥 Founding Member Deal
                                                </h3>
                                                <p className="text-white/60">
                                                    Pay $29 once for lifetime Pro access. Limited to 100 members only!
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setShowLifetimeModal(true)}
                                            className="whitespace-nowrap px-6 py-2.5 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity flex items-center gap-2"
                                        >
                                            <Star className="w-4 h-4" />
                                            Get Lifetime Access
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Upgrade Modal */}
                            <UpgradeModal
                                isOpen={showUpgradeModal}
                                onClose={() => setShowUpgradeModal(false)}
                            />

                            {/* Lifetime Checkout Modal */}
                            <LifetimeCheckoutModal
                                isOpen={showLifetimeModal}
                                onClose={() => setShowLifetimeModal(false)}
                                userEmail={user?.email}
                            />
                        </div>
                    </div>
                </main>

                {/* Feedback Widget */}
                <FeedbackWidget user={user} />

                {/* Voice Guide Assistant */}
                <VoiceGuide />
            </div>
        </NotificationProvider>
    )
}
